"use client";

import InitialsComponent from "@/components/dashboard/general/svgs/InitialsComponent";
import IconComponent from "@/components/icon/IconComponent";
import LonghouseNotificationBell from "@/components/notifications/LonghouseNotificationBell";
import { classNames } from "@/helpers/common/classNames";
import { toggleFullScreen } from "@/helpers/common/toggleFullScreen";
import { useAuth } from "@/hooks/auth/useAuth";
import { useTheme } from "@/hooks/general/useTheme";
import { Menu, Transition } from "@headlessui/react";
import {
    Bars3Icon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ViewfinderCircleIcon,
} from "@heroicons/react/24/outline";
import React, { Fragment, forwardRef, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface TopNavProps {
    setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
    toggleSidebarCollapse: () => void;
    collapsed: boolean;
}

const TopNav = forwardRef<HTMLDivElement, TopNavProps>(
    ({ setSidebarOpen, toggleSidebarCollapse, collapsed }, ref) => {
        const { toggleTheme } = useTheme();
        const { user, logoutUser } = useAuth();
        const userName = user?.twitchUsername || "Vanderheim Admin";

        const [currentPage, setCurrentPage] = useState("/dashboard");
        const navigate = useNavigate();
        const location = useLocation();

        useEffect(() => {
            if (!location.pathname.includes("/search")) {
                setCurrentPage(location.pathname);
            }
        }, [location]);

        return (
            <nav
                ref={ref}
                className="relative flex items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
            >
                {/* Left side buttons (mobile + desktop collapse) */}
                <div className="flex items-center gap-2">
                    {/* Mobile: open sidebar */}
                    <button
                        type="button"
                        className="-m-2.5 p-2.5 lg:hidden dark:text-white"
                        onClick={() => setSidebarOpen(true)}
                    >
                        <span className="sr-only">Open sidebar</span>
                        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                    </button>

                    <div className="group relative">
                        <button
                            type="button"
                            className="hidden items-center p-2.5 text-slate-700 hover:text-black lg:inline-flex dark:text-white dark:hover:text-slate-200"
                            onClick={toggleSidebarCollapse}
                        >
                            <span className="sr-only">
                                {collapsed
                                    ? "Expand sidebar"
                                    : "Collapse sidebar"}
                            </span>
                            {collapsed ? (
                                <ChevronRightIcon className="h-6 w-6" />
                            ) : (
                                <ChevronLeftIcon className="h-6 w-6" />
                            )}
                        </button>
                        <span className="pointer-events-none absolute top-1/2 left-full ml-2 -translate-y-1/2 rounded bg-slate-800 px-2 py-1 text-xs whitespace-nowrap text-white opacity-0 group-hover:opacity-100">
                            {collapsed ? "Expand" : "Collapse"}
                        </span>
                    </div>
                </div>

                {/* Right-side layout */}
                <div className="flex flex-1 items-center justify-end self-stretch">
                    <div className="flex items-center gap-x-4 lg:gap-x-6">
                        <LonghouseNotificationBell />

                        <button
                            type="button"
                            onClick={toggleFullScreen}
                            className="-m-2.5 hidden p-2.5 md:block dark:text-white dark:hover:text-white"
                        >
                            <span className="sr-only">Full Screen</span>
                            <ViewfinderCircleIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                            />
                        </button>

                        <button
                            type="button"
                            className="-m-2.5 p-2.5 dark:text-white dark:hover:text-white"
                            onClick={() => toggleTheme()}
                        >
                            <span className="sr-only">Toggle theme</span>
                            <IconComponent
                                iconName="light-bulb-solid"
                                className="h-6 w-6"
                                aria-hidden="true"
                            />
                        </button>

                        <Menu as="div" className="relative">
                            <Menu.Button className="-m-1.5 flex items-center p-1.5">
                                <span className="sr-only">Open user menu</span>
                                <InitialsComponent name={userName} />
                                <span className="hidden lg:flex lg:items-center">
                                    <span
                                        className="ml-4 text-sm leading-6 font-semibold dark:text-white"
                                        aria-hidden="true"
                                    >
                                        {userName}
                                    </span>
                                    <IconComponent
                                        iconName="chevron-down-solid-20"
                                        className="ml-2 h-5 w-5 dark:text-white"
                                        aria-hidden={true}
                                    />
                                </span>
                            </Menu.Button>
                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <Menu.Items className="ring-brand absolute -right-3.5 z-30 mt-2.5 w-44 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 focus:outline-hidden dark:bg-slate-800 dark:ring-slate-800/5">
                                    <Menu.Item>
                                        <Link
                                            to="/dashboard"
                                            className={classNames(
                                                "block px-3 py-1 text-sm leading-6 dark:text-white",
                                                currentPage ===
                                                    "/dashboard/profile"
                                                    ? "bg-gray-100 dark:bg-slate-700"
                                                    : "",
                                            )}
                                        >
                                            Back to Village
                                        </Link>
                                    </Menu.Item>
                                    <Menu.Item>
                                        <Link
                                            to="/longhouse/inbox"
                                            className={classNames(
                                                "block px-3 py-1 text-sm leading-6 dark:text-white",
                                                currentPage ===
                                                    "/longhouse/inbox"
                                                    ? "bg-gray-100 dark:bg-slate-700"
                                                    : "",
                                            )}
                                        >
                                            Inbox
                                        </Link>
                                    </Menu.Item>
                                    <Menu.Item>
                                        <Link
                                            to="/dashboard/surveys"
                                            className={classNames(
                                                "block px-3 py-1 text-sm leading-6 dark:text-white",
                                                currentPage ===
                                                    "/dashboard/surveys"
                                                    ? "bg-gray-100 dark:bg-slate-700"
                                                    : "",
                                            )}
                                        >
                                            Surveys
                                        </Link>
                                    </Menu.Item>
                                    <Menu.Item>
                                        <Link
                                            to="/dashboard/my-account"
                                            className={classNames(
                                                "block px-3 py-1 text-sm leading-6 dark:text-white",
                                                currentPage ===
                                                    "/dashboard/my-account"
                                                    ? "bg-gray-100 dark:bg-slate-700"
                                                    : "",
                                            )}
                                        >
                                            My Account
                                        </Link>
                                    </Menu.Item>
                                    <Menu.Item>
                                        <Link
                                            to="#"
                                            onClick={logoutUser}
                                            className={classNames(
                                                "block px-3 py-1 text-sm leading-6 dark:text-white",
                                            )}
                                        >
                                            Sign Out
                                        </Link>
                                    </Menu.Item>
                                </Menu.Items>
                            </Transition>
                        </Menu>
                    </div>
                </div>
            </nav>
        );
    },
);

TopNav.displayName = "TopNav";
export default TopNav;
